"use client";

import * as React from "react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type {
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  VisibilityState,
  FilterFn,
} from "@tanstack/react-table";

import {
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RefreshCw } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { deleteData, deleteDataById, syncPayload } from "./actions";
import type { Payment } from "./column";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  setData: React.Dispatch<React.SetStateAction<TData[]>>;
  onRefresh?: () => void;
}

// Custom filter function for date range
const dateFilter: FilterFn<any> = (row, columnId, filterValue) => {
  if (!filterValue) {
    return true;
  }

  const rowValue = row.getValue(columnId);
  const rowDate = new Date(rowValue as string);
  const [start, end] = filterValue as [Date | null, Date | null];

  if (start && !end) {
    return rowDate >= start;
  } else if (!start && end) {
    return rowDate <= end;
  } else if (start && end) {
    return rowDate >= start && rowDate <= end;
  }
  return true;
};

export function DataTable<TData, TValue>({
  columns,
  data,
  setData,
  onRefresh,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    []
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({
      StatusCode: false,
      StatusMessage: false,
      SignatureKey: false,
      PaymentType: false,
      MerchantID: false,
      Currency: false,
      FraudStatus: false,
      DeletedAt: false,
    });
  const [rowSelection, setRowSelection] = React.useState({});
  const [timeFilter, setTimeFilter] = React.useState("all");
  const [isBatchSyncing, setIsBatchSyncing] = React.useState(false);

  const modifiedColumns = React.useMemo(() => {
    return columns.map((col) => {
      const c = col as any;
      if (c.accessorKey === "TransactionTime") {
        return {
          ...col,
          filterFn: dateFilter,
        };
      }
      return col;
    });
  }, [columns]);

  const table = useReactTable({
    data,
    columns: modifiedColumns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
  });

  const handleTimeFilterChange = (value: string) => {
    setTimeFilter(value);
    const now = new Date();
    let startDate: Date | null = null;
    if (value === "24h") {
      startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    } else if (value === "7d") {
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    } else if (value === "30d") {
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }
    table
      .getColumn("TransactionTime")
      ?.setFilterValue(startDate ? [startDate, null] : null);
  };

  const isFiltered = table.getState().columnFilters.length > 0;

  const settlementTotal = React.useMemo(() => {
    return table
      .getFilteredRowModel()
      .rows.filter(
        (row) => (row.original as any).TransactionStatus === "settlement"
      )
      .reduce((total, row) => {
        const amount = parseFloat((row.original as any).GrossAmount || "0");
        return total + amount;
      }, 0);
  }, [table.getFilteredRowModel().rows]);

  return (
    <div>
      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Tanggal Hari Ini
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Date().toLocaleDateString("id-ID", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Jumlah Transaksi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {table.getFilteredRowModel().rows.length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Settlement
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {new Intl.NumberFormat("id-ID", {
                style: "currency",
                currency: "IDR",
              }).format(settlementTotal)}
            </div>
          </CardContent>
        </Card>
      </div>
      <div className="flex flex-wrap items-center py-4 gap-2">
        <Input
          placeholder="Filter Invoice..."
          value={(table.getColumn("OrderID")?.getFilterValue() as string) ?? ""}
          onChange={(event) =>
            table.getColumn("OrderID")?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">Status</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={
                (table
                  .getColumn("TransactionStatus")
                  ?.getFilterValue() as string) ?? ""
              }
              onValueChange={(value) =>
                table.getColumn("TransactionStatus")?.setFilterValue(value)
              }
            >
              <DropdownMenuRadioItem value="settlement">
                Settlement
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="pending">
                Pending
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="expire">
                Expire
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="cancel">
                Cancel
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="deny">Deny</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() =>
                table.getColumn("TransactionStatus")?.setFilterValue(undefined)
              }
            >
              Clear Filter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline">Transaction Time</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuLabel>Filter by Time</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={timeFilter}
              onValueChange={handleTimeFilterChange}
            >
              <DropdownMenuRadioItem value="all">
                All Time
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="24h">
                Last 24 hours
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="7d">
                Last 7 days
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="30d">
                Last 30 days
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        {isFiltered && (
          <Button
            variant="ghost"
            onClick={() => table.resetColumnFilters()}
            className="h-8 px-2 lg:px-3"
          >
            Reset
          </Button>
        )}

        {table.getSelectedRowModel().rows.length > 0 && (
          <>
            <Button
              variant="default"
              disabled={isBatchSyncing}
              onClick={async () => {
                const selectedRows = table.getSelectedRowModel().rows;
                const selectedIds = selectedRows.map(
                  (row) => (row.original as any).OrderID
                );
                setIsBatchSyncing(true);
                try {
                  const results = await Promise.all(
                    selectedIds.map((id) => syncPayload(id))
                  );
                  const failed = results.filter((r) => !r.success);
                  if (failed.length === 0) {
                    alert(`Berhasil sinkronisasi ${selectedIds.length} payload ke semua destination aktif.`);
                  } else {
                    alert(`${selectedIds.length - failed.length} payload berhasil disinkronisasi, ${failed.length} gagal.`);
                  }
                  table.resetRowSelection();
                } catch (err) {
                  console.error("Gagal sinkronisasi batch:", err);
                  alert("Terjadi kesalahan saat sinkronisasi batch.");
                } finally {
                  setIsBatchSyncing(false);
                }
              }}
              className="gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <RefreshCw className={`w-4 h-4 ${isBatchSyncing ? "animate-spin" : ""}`} />
              {isBatchSyncing ? "Menyinkronkan..." : `Sync (${table.getSelectedRowModel().rows.length}) data`}
            </Button>

            <Button
              variant="destructive"
              onClick={async () => {
                const selectedRows = table.getSelectedRowModel().rows;
                const selectedIds = selectedRows.map(
                  (row) => (row.original as any).OrderID
                );

                try {
                  await Promise.all(selectedIds.map((id) => deleteDataById(id)));

                  // update data source
                  setData((prev) =>
                    prev.filter((item) => !selectedIds.includes((item as any).OrderID))
                  );

                  table.resetRowSelection();
                } catch (error) {
                  console.error("Gagal menghapus data:", error);
                }
              }}
            >
              Hapus data ({table.getSelectedRowModel().rows.length})
            </Button>
          </>
        )}

        <Button variant={"destructive"} onClick={() => deleteData()}>
          Hapus semua data
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="ml-auto">
              Columns
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {table
              .getAllColumns()
              .filter((column) => column.getCanHide())
              .map((column) => {
                return (
                  <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) =>
                      column.toggleVisibility(!!value)
                    }
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                );
              })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className="overflow-hidden rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between">
        <div className="text-muted-foreground text-sm">
          {table.getFilteredSelectedRowModel().rows.length} of{" "}
          {table.getFilteredRowModel().rows.length} row(s) selected.
        </div>
        <div className="flex items-center justify-end space-x-2 py-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
